<template>
    <DCardBody>
        <DForm @submit.prevent="submitForm">
            <input
                type="hidden"
                name="id"
                :value="form.id"
            >
            <DFormRow
                :label="__('products.dnsTemplate.form.name')"
                :is-required="true"
                :error="errors.name"
                :hint="__('products.dnsTemplate.form.name_hint')"
            >
                <DInput
                    id="name"
                    v-model="form.name"
                    :is-valid="isFormFieldValid('name')"
                    maxlength="45"
                />
            </DFormRow>
            <DFormRow
                :label="__('products.dnsTemplate.form.style')"
                :is-required="true"
                :hint="__('products.dnsTemplate.form.style_hint')"
            >
                <DSelect
                    v-model="form.style"
                    :options="nameserverStyle"
                    :value="form.style"
                    @update:value="changeStyle"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'default'"
                :label="__('products.dnsTemplate.form.dns_provider')"
                :is-required="true"
                :hint="__('products.dnsTemplate.form.dns_provider_hint')"
            >
                <DSelect
                    v-model="form.dns_provider"
                    :options="dnsProviders"
                    :value="form.dns_provider"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'default'"
                :label="__('products.dnsTemplate.form.template')"
                :is-required="true"
                :error="is_valid_json ? errors.template_data : __('products.dnsTemplate.form.invalidJSONMessage')"
                :hint="__('products.dnsTemplate.form.template_hint')"
            >
                <DFormTextarea
                    id="template_data"
                    v-model="form.template_data"
                    rows="10"
                    :is-valid="isFormFieldValid('template_data')"
                    @input="checkValidJSON"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.first_nameserver')"
                :is-required="true"
                :error="errors.first_nameserver"
                :hint="__('products.dnsTemplate.form.first_nameserver_hint')"
            >
                <DInput
                    id="first_nameserver"
                    v-model="form.first_nameserver"
                    :is-valid="isFormFieldValid('first_nameserver')"
                    maxlength="100"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.second_nameserver')"
                :is-required="true"
                :error="errors.second_nameserver"
                :hint="__('products.dnsTemplate.form.second_nameserver_hint')"
            >
                <DInput
                    id="second_nameserver"
                    v-model="form.second_nameserver"
                    :is-valid="isFormFieldValid('second_nameserver')"
                    maxlength="100"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.third_nameserver')"
                :is-required="false"
                :error="errors.third_nameserver"
                :hint="__('products.dnsTemplate.form.third_nameserver_hint')"
            >
                <DInput
                    id="third_nameserver"
                    v-model="form.third_nameserver"
                    :is-valid="isFormFieldValid('third_nameserver')"
                    maxlength="100"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.fourth_nameserver')"
                :is-required="false"
                :error="errors.fourth_nameserver"
                :hint="__('products.dnsTemplate.form.fourth_nameserver_hint')"
            >
                <DInput
                    id="fourth_nameserver"
                    v-model="form.fourth_nameserver"
                    :is-valid="isFormFieldValid('fourth_nameserver')"
                    maxlength="100"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.fifth_nameserver')"
                :is-required="false"
                :error="errors.fifth_nameserver"
                :hint="__('products.dnsTemplate.form.fifth_nameserver_hint')"
            >
                <DInput
                    id="fifth_nameserver"
                    v-model="form.fifth_nameserver"
                    :is-valid="isFormFieldValid('fifth_nameserver')"
                    maxlength="100"
                />
            </DFormRow>
            <DFormRow
                v-if="form.style === 'divergent'"
                :label="__('products.dnsTemplate.form.are_divergent_nameservers_external')"
                :is-required="false"
                :error="errors.are_divergent_nameservers_external"
                :hint="__('products.dnsTemplate.form.are_divergent_nameservers_external_hint')"
            >
                <DSwitch
                    id="switch-retired"
                    v-model="form.are_divergent_nameservers_external"
                    color="success"
                    variant="3d"
                />
            </DFormRow>

            <DFormControls
                :disabled="!is_valid_json"
                :cancel-route="$route('products.dns-templates.index')"
                @submit="submitForm"
            />
        </DForm>
    </DCardBody>
</template>

<script>
export default {
    name: 'DNSTemplateForm',
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        dnsTemplate: {
            type: Object,
            default () {
                return {}
            }
        },
        nameserverStyle: {
            type: Array,
            default () {
                return []
            }
        },
        dnsProviders: {
            type: Array,
            default () {
                return []
            }
        }
    },
    emits: ['submit'],
    data () {
        return {
            form: this.$inertia.form({
                id: null,
                name: this.dnsTemplate?.name,
                style: this.dnsTemplate?.style,
                dns_provider: this.dnsTemplate?.dns_provider,
                first_nameserver: this.dnsTemplate?.first_nameserver,
                second_nameserver: this.dnsTemplate?.second_nameserver,
                third_nameserver: this.dnsTemplate?.third_nameserver,
                fourth_nameserver: this.dnsTemplate?.fourth_nameserver,
                fifth_nameserver: this.dnsTemplate?.fifth_nameserver,
                template: '',
                template_data: JSON.stringify(this.dnsTemplate?.template, null, 6) ?? null,
                are_divergent_nameservers_external: this.dnsTemplate?.are_divergent_nameservers_external ?? false
            }),
            is_valid_json: true
        }
    },
    mounted () {
        if (Object.keys(this.dnsTemplate).length !== 0) {
            this.form.id = this.dnsTemplate.id
            this.form.name = this.dnsTemplate.name
            this.form.template = this.dnsTemplate.template

            this.checkValidJSON()
        }
    },
    methods: {
        submitForm () {
            this.$emit('submit', this.form)
        },
        checkValidJSON () {
            try {
                JSON.parse(this.form.template_data)
                this.is_valid_json = true
            } catch (e) {
                this.is_valid_json = false
            }
        },
        changeStyle (value) {
            if (value === 'default') {
                this.form.first_nameserver = null
                this.form.second_nameserver = null
                this.form.third_nameserver = null
                this.form.fourth_nameserver = null
                this.form.fifth_nameserver = null
                this.form.are_divergent_nameservers_external = false
            } else {
                this.form.template_data = null
                this.form.dns_provider = null
            }
        },
        isStyleProvider () {
            console.log(this.form.style === 'default')
            return this.form.style === 'default'
        }
    }
}
</script>
